import { useState } from 'react';
import { LayoutTemplate, Save, X } from 'lucide-react';

export interface VariantOption {
  _id: string;
  name: string;
  /** Number of field mappings currently on the variant. Informational only. */
  fieldMappingCount?: number;
  /** Last-run timestamp for the variant (display only). */
  lastRunAt?: string;
}

export interface SaveAsVariantCardProps {
  /**
   * Name of the shell template the user just ran against. Used for labelling.
   * If the caller can't identify the shell (edge case), leave undefined
   * and the card will render a generic title.
   */
  shellName?: string;
  /** Existing variants of the shell the user may choose to overwrite. */
  variants: VariantOption[];
  /** Whether a save operation is currently in flight. Disables the buttons. */
  saving?: boolean;
  /** Surface the most recent save error inline. */
  error?: string | null;
  /**
   * User picked "Save as new variant". Caller is expected to create a new
   * template carrying the current run's mappings + `parentTemplateId` set
   * to the shell.
   */
  onSaveAsNew: (name: string) => void | Promise<void>;
  /** User picked "Update existing variant". Caller overwrites its mappings. */
  onUpdateVariant: (variantId: string) => void | Promise<void>;
  /** User picked "Don't save". Caller may collapse the card or hide it. */
  onDismiss: () => void;
}

type Choice = 'new' | 'update' | 'skip' | null;

/**
 * Card shown after a shell-based import run completes successfully, giving
 * the user three ways to preserve the mapping work they just did:
 *   - Save as a new variant (names it; links it to the shell)
 *   - Overwrite an existing variant's mappings
 *   - Discard — the run is recorded regardless, mappings are not saved
 *
 * The card is intentionally stateful rather than stateless: the radio
 * selection, the new-variant name, and the selected variant id all live
 * here so the host wizard can stay dumb.
 */
export default function SaveAsVariantCard({
  shellName,
  variants,
  saving = false,
  error,
  onSaveAsNew,
  onUpdateVariant,
  onDismiss,
}: SaveAsVariantCardProps) {
  const [choice, setChoice] = useState<Choice>(null);
  const [newName, setNewName] = useState<string>(
    shellName ? `${shellName} — ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : '',
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');

  const canSave =
    !saving &&
    ((choice === 'new' && newName.trim().length > 0) ||
      (choice === 'update' && !!selectedVariantId) ||
      choice === 'skip');

  const handleSubmit = () => {
    if (!canSave) return;
    if (choice === 'new') {
      onSaveAsNew(newName.trim());
    } else if (choice === 'update') {
      onUpdateVariant(selectedVariantId);
    } else if (choice === 'skip') {
      onDismiss();
    }
  };

  return (
    <div
      className="border border-purple-200 bg-purple-50/40 rounded-lg p-4 space-y-3"
      data-testid="save-as-variant-card"
    >
      <div className="flex items-center gap-2">
        <LayoutTemplate className="h-4 w-4 text-purple-600" />
        <h3 className="text-sm font-semibold text-gray-900">
          Save these mappings for next time?
        </h3>
      </div>
      <p className="text-xs text-gray-600">
        You just imported using a{shellName ? <> shell ({<strong>{shellName}</strong>}){' '}</> : ' shell'}
        template. If you expect to see this same file shape again, save the mappings as a named{' '}
        <strong>variant</strong> of the shell so future imports skip the mapping step.
      </p>

      <div className="space-y-2 text-xs">
        {/* New variant */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="save-variant-choice"
            className="mt-0.5"
            checked={choice === 'new'}
            onChange={() => setChoice('new')}
            data-testid="save-variant-new"
          />
          <div className="flex-1 space-y-1.5">
            <div className="font-medium text-gray-800">Save as a new variant</div>
            {choice === 'new' && (
              <input
                type="text"
                className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-purple-500"
                placeholder="e.g. Monthly County Dump — Q2 2026"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                data-testid="save-variant-name"
              />
            )}
          </div>
        </label>

        {/* Update existing */}
        <label className={`flex items-start gap-2 ${variants.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
          <input
            type="radio"
            name="save-variant-choice"
            className="mt-0.5"
            checked={choice === 'update'}
            onChange={() => setChoice('update')}
            disabled={variants.length === 0}
            data-testid="save-variant-update"
          />
          <div className="flex-1 space-y-1.5">
            <div className="font-medium text-gray-800">
              Update an existing variant
              {variants.length === 0 && (
                <span className="ml-1 text-gray-400 font-normal">(no existing variants)</span>
              )}
            </div>
            {choice === 'update' && variants.length > 0 && (
              <select
                className="w-full max-w-sm border border-gray-300 rounded-md px-3 py-1.5 text-sm text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-purple-500"
                value={selectedVariantId}
                onChange={e => setSelectedVariantId(e.target.value)}
                data-testid="save-variant-select"
              >
                <option value="">— Select a variant —</option>
                {variants.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.name}
                    {typeof v.fieldMappingCount === 'number' ? ` (${v.fieldMappingCount} mappings)` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </label>

        {/* Skip */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="save-variant-choice"
            className="mt-0.5"
            checked={choice === 'skip'}
            onChange={() => setChoice('skip')}
            data-testid="save-variant-skip"
          />
          <div className="flex-1">
            <div className="font-medium text-gray-800">Don't save — this was a one-off</div>
            <div className="text-[11px] text-gray-500">
              The run is recorded regardless; only the column mappings are discarded.
            </div>
          </div>
        </label>
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSave}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="save-variant-submit"
        >
          {choice === 'skip' ? <X className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
          {saving
            ? 'Saving…'
            : choice === 'new'
              ? 'Save variant'
              : choice === 'update'
                ? 'Update variant'
                : choice === 'skip'
                  ? 'Dismiss'
                  : 'Choose an option'}
        </button>
      </div>
    </div>
  );
}
